import {
  PersistedFormGroup,
  PersistedFormArray,
  PersistedArray,
} from './persisted-form-group';
import type {
  PersistedFormGroupOptions,
  PersistedFormArrayOptions,
  PersistedArrayOptions,
} from './persisted-form-group';
import type { SchemaFormControl } from '@apexdesigner/schema-forms';
import { Horse } from './horse';
export { Horse };
import { horseSchema } from '@schemas/business-objects/horse';
import { GroupMatchFormArray } from './group-match-form-group';
import { HorseGroupMemberFormArray } from './horse-group-member-form-group';
import { MatchFormArray } from './match-form-group';

export class HorseFormGroup extends PersistedFormGroup {
  declare controls: {
    id: SchemaFormControl;
    name: SchemaFormControl;
    sex: SchemaFormControl;
    phenotype: SchemaFormControl;
    sireName: SchemaFormControl;
    owner: SchemaFormControl;
    yearOfBirth: SchemaFormControl;
    bmeScore: SchemaFormControl;
    studFee: SchemaFormControl;
    state: SchemaFormControl;
    country: SchemaFormControl;
    active: SchemaFormControl;
    height: SchemaFormControl;
    heartGirth: SchemaFormControl;
    length: SchemaFormControl;
    flank: SchemaFormControl;
    frontLeg: SchemaFormControl;
    backPastern: SchemaFormControl;
    frontPastern: SchemaFormControl;
    back: SchemaFormControl;
    hipToHock: SchemaFormControl;
    hockToGround: SchemaFormControl;
    ilium: SchemaFormControl;
    tuberCalcis: SchemaFormControl;
    gaskin: SchemaFormControl;
    tibia: SchemaFormControl;
    femur: SchemaFormControl;
    bodyLength: SchemaFormControl;
    legacyId: SchemaFormControl;
    groupMemberships: HorseGroupMemberFormArray;
    matchesAsMare: MatchFormArray;
    matchesAsStallion: MatchFormArray;
    groupMatches: GroupMatchFormArray;
  };

  declare value: Partial<Horse>;

  constructor(
    data?: Record<string, any> | null,
    options?: PersistedFormGroupOptions,
  ) {
    super(horseSchema, Horse, data, options);
  }

  get object(): Horse {
    return new Horse(this.getRawValue());
  }

  protected override createControl(name: string) {
    switch (name) {
      case 'groupMemberships':
        return new HorseGroupMemberFormArray();
      case 'matchesAsMare':
        return new MatchFormArray();
      case 'matchesAsStallion':
        return new MatchFormArray();
      case 'groupMatches':
        return new GroupMatchFormArray();
      default:
        return undefined;
    }
  }

  static async find(filter?: any): Promise<Horse[]> {
    return Horse.find(filter);
  }

  static async findOne(filter?: any): Promise<Horse | null> {
    return Horse.findOne(filter);
  }

  static async findById(id: number, filter?: any): Promise<Horse> {
    return Horse.findById(id, filter);
  }

  static async create(data: Partial<Horse>): Promise<Horse> {
    return Horse.create(data as any);
  }

  static async updateById(id: number, updates: Partial<Horse>): Promise<Horse> {
    return Horse.updateById(id, updates as any);
  }

  static async deleteById(id: number): Promise<boolean> {
    return Horse.deleteById(id);
  }

  static async countMatching(where?: Record<string, any>): Promise<number> {
    return Horse.countMatching(where);
  }
}

export class HorseFormArray extends PersistedFormArray {
  declare controls: HorseFormGroup[];

  readonly entityName = 'Horse' as const;

  constructor(options?: PersistedFormArrayOptions) {
    super(horseSchema, Horse, options);
  }

  protected override createItemGroup() {
    return new HorseFormGroup();
  }

  get array(): Horse[] {
    return this.controls.map((group: HorseFormGroup) => group.object);
  }
}

export class HorsePersistedArray extends PersistedArray<Horse> {
  readonly entityName = 'Horse' as const;

  constructor(options?: PersistedArrayOptions) {
    super(Horse, options);
  }
}
